import { ClipboardEvent, KeyboardEvent, SyntheticEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import Autocomplete from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";

import Chip from "features/components/Chip";
import TextField from "features/components/TextField";
import { FILTER_PASTE_DELIMITER } from "features/ui/Filters/constants";
import SearchIcon from "features/ui/Icons/SearchIcon";

import { handleSelectAllAndCopyKeyDown } from "./SelectFilter/utils";

interface Props {
  fieldName: string;
  currentFilterValues: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  searchIcon?: boolean;
}

const ContainsStartsWithFilter = ({
  fieldName,
  currentFilterValues,
  onChange,
  disabled,
  autoFocus,
  searchIcon,
}: Props) => {
  const { t } = useTranslation("filters");
  const [searchInput, setSearchInput] = useState("");

  const handleOnPaste = ({ target, clipboardData }: ClipboardEvent) => {
    const text = clipboardData.getData("Text");
    const splitValues = text.split(FILTER_PASTE_DELIMITER);
    const newValues = splitValues.filter(Boolean).map((value) => value.trim());
    onChange([...currentFilterValues, ...newValues]);

    // also unfocus the input so that input values are cleared
    // (if we don't do this we get N chips + N values unconfirmed values in the input field)
    (target as HTMLInputElement).blur();
  };

  const handleBlur = () => {
    if (searchInput.trim() === "") return;

    onChange([...currentFilterValues, searchInput.trim()]);
    setSearchInput("");
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && searchInput.trim() !== "") {
      event.stopPropagation();
      event.preventDefault();
      onChange([...currentFilterValues, searchInput.trim()]);
      setSearchInput("");
    } else {
      handleSelectAllAndCopyKeyDown(t, event, currentFilterValues);
    }
  };

  return (
    <Autocomplete
      autoFocus={autoFocus}
      multiple
      disableCloseOnSelect={true}
      autoHighlight={true}
      disabled={disabled}
      freeSolo
      open={false}
      onBlur={handleBlur}
      data-testid={`${fieldName}-autocomplete`}
      options={[]}
      limitTags={2}
      fullWidth
      value={currentFilterValues}
      inputValue={searchInput}
      onInputChange={(_, newInputValue) => {
        setSearchInput(newInputValue);
      }}
      onPaste={handleOnPaste}
      onChange={(_: SyntheticEvent, values: string[]) => {
        onChange(values);
      }}
      renderValue={(value: readonly string[], getItemProps) =>
        value.map((option: string, index: number) => (
          <Chip label={option} {...getItemProps({ index })} />
        ))
      }
      renderInput={(params) => (
        <TextField
          autoFocus={autoFocus}
          {...params}
          placeholder={
            !searchIcon ? t("placeholders.type_or_paste") : undefined
          }
          variant="outlined"
          onPaste={handleOnPaste}
          onKeyDown={handleKeyDown}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              startAdornment: searchIcon ? (
                <>
                  <InputAdornment position="start">
                    <SearchIcon className="text-gray-400 ml-1" />
                  </InputAdornment>
                  {params.slotProps.input.startAdornment}
                </>
              ) : (
                params.slotProps.input.startAdornment
              ),
            },
          }}
        />
      )}
    />
  );
};

export default ContainsStartsWithFilter;
